import { capitalizeName } from '@getpopsure/public-utility';
import routes from 'constants/routes';
import dayjs from 'dayjs';
import { getPoliciesListAction } from 'features/policiesDashboard/utils/getPoliciesListAction';
import { getPolicyStatus } from 'features/policiesDashboard/utils/getPolicyStatus';
import {
  bannerColorMapping,
  getTitleMapping,
} from 'models/insurances/types/mapping';
import { generatePath } from 'react-router';

import type { PolicySingleModal } from '../../components/modals';
import {
  CancelPolicyModal,
  DelinquencyBlockerCancelPolicyModal,
  DelinquencyBlockerCreateClaimModal,
} from '../../components/modals';
import { DATE_FORMAT } from '../../constants';
import {
  BillingType,
  GetPolicyConfigArgs,
  PolicyConfig,
  PolicySingleSection,
} from '../../PolicySingle.models';
import { getPolicyDocuments } from '../../sections/PolicyDocuments/utils/getPolicyDocuments';
import { getExpatSpainButtons } from './getExpatSpainButtons';

export const getExpatSpainConfig = (
  args: GetPolicyConfigArgs
): PolicyConfig => {
  const [policy, t] = args;

  const { policyNumber, insuredPerson, startDate, price, billingType } =
    policy.attributes;

  const action = getPoliciesListAction(policy);

  const policyStatus =
    action && action !== 'DETAIL'
      ? 'ACTION_NEEDED'
      : getPolicyStatus(policy, true);

  const capitalizedName = insuredPerson?.name
    ? capitalizeName(insuredPerson.name)
    : '';

  const subtitle = capitalizedName || '';

  const header: PolicyConfig['header'] = {
    title: getTitleMapping(t).INCOMING_ES,
    subtitle,
    backgroundColor: bannerColorMapping.INCOMING_ES,
    insuranceType: policy.type,
    policyStatus,
    startDate,
  };

  if (price) {
    header.price = {
      amount: parseFloat(price),
      billingType: billingType as BillingType,
    };
  }

  const sections: PolicySingleSection[] = [
    {
      id: 'POLICY_DETAILS',
      props: {
        policyType: policy.type,
        info: [
          {
            title: t('myPolicies.policyDetails.policyNumber', 'Policy number'),
            value: policyNumber,
          },
          {
            title: t(
              'myPolicies.policyDetails.insuredPerson',
              'Insured person'
            ),
            value: insuredPerson && capitalizeName(insuredPerson.name),
          },
          {
            title: t('myPolicies.policyDetails.startDate', 'Start date'),
            value: startDate && dayjs(startDate).format(DATE_FORMAT),
          },
        ],
        buttons: getExpatSpainButtons(policy),
      },
    },
    {
      id: 'POLICY_CLAIMS',
      props: {
        claims: policy.claims,
        route: generatePath(routes.claims.expatSpain.path, {
          policyId: policy.id,
        }),
        insuranceType: 'INCOMING_ES',
        activeUntil:
          policy.status === 'ACTIVE'
            ? undefined
            : policy.attributes.activeUntil,
        policyStatus: policy.status,
        screen: {
          button: {
            title: t(
              'myPolicies.policyDetails.claimsButton.expatSpain.caption',
              'New claim or cost plan'
            ),
            dataCy: 'expat-spain-policy-detail-make-a-claim',
          },
        },
      },
    },
    {
      id: 'POLICY_DOCUMENTS',
      props: {
        documents: getPolicyDocuments(policy),
      },
    },
  ];

  const modals: PolicySingleModal[] = [
    {
      id: 'CANCEL_POLICY',
      component: CancelPolicyModal,
    },
    {
      id: 'DELINQUENT_BLOCKER_CANCEL_POLICY',
      component: DelinquencyBlockerCancelPolicyModal,
    },
    {
      id: 'DELINQUENT_BLOCKER_CREATE_CLAIM',
      component: DelinquencyBlockerCreateClaimModal,
    },
  ];

  return { header, modals, sections };
};
